import { useQuery, useMutation } from '@tanstack/react-query';
import { searchProducts } from '../api/products';
import { searchIngredients } from '../api/ingredients';

export const useUnifiedSearch = (query, productType = 'all', enabled = true) =>
  useQuery({
    queryKey: ['items', 'search', query, productType],
    queryFn: async () => {
      if (productType === 'product') return searchProducts(query, 20);
      if (productType === 'ingredient') return searchIngredients(query, 20);
      const [products, ingredients] = await Promise.all([
        searchProducts(query, 10),
        searchIngredients(query, 10),
      ]);
      return [...products, ...ingredients];
    },
    enabled: enabled && query.trim().length >= 1,
    placeholderData: (prev) => prev,
  });

// Look a scanned/typed barcode up against the same business-scoped search the
// dropdown uses (regex over sku/name/barcode) instead of the single exact-match
// `/products/barcode/:code` endpoint. That endpoint only returns one row and
// only on an exact array-equality match, so any formatting difference — or a
// barcode shared by several items — wrongly fell through to "not found". Here we
// return ALL matches so the caller can auto-select a single hit or let the user
// pick when multiple items share the code.
export const useBarcodeLookup = () =>
  useMutation({
    mutationFn: async (barcode) => {
      const code = String(barcode || '').trim();
      if (!code) return { found: false, items: [] };

      const [productsResult, ingredientsResult] = await Promise.allSettled([
        searchProducts(code, 20),
        searchIngredients(code, 20),
      ]);
      const products =
        productsResult.status === 'fulfilled' ? productsResult.value : [];
      const ingredients =
        ingredientsResult.status === 'fulfilled' ? ingredientsResult.value : [];
      const all = [...products, ...ingredients];

      // Prefer rows where the code actually appears in a barcode or SKU (a
      // scanned code should not match purely on a name substring). Fall back to
      // the raw search hits so we never claim "not found" when the search did
      // surface candidates.
      const byIdentifier = all.filter(
        (it) =>
          (it.barcodes || []).some((b) => String(b).includes(code)) ||
          String(it.sku || '').includes(code)
      );
      const items = byIdentifier.length ? byIdentifier : all;

      return { found: items.length > 0, items };
    },
  });
